import React, {FC, useEffect, useMemo, useRef} from 'react';
import {DFPSlotsProvider, DFPManager} from 'react-dfp';
import {useLocation} from 'react-router';
import {ReactTitle} from 'react-meta-tags';
import GA from 'react-ga';
import GA4 from 'react-ga4';

import {byTerm, searchTermIsSport, selectMatches} from 'utils/matchFns';
import {DateTabsType, SearchTerm, Sport} from 'utils/types';
import breakpoints from 'utils/breakpoints';
// components
import Ad from 'components/Common/Ad';
import Menu from 'components/Common/Menu';
import SearchBar from 'components/Matchcenter/SearchBar';
import DateTabs from 'components/Matchcenter/DateTabs';
import TabbedMatchLists from 'components/Matchcenter/TabbedMatchLists';
import WidgetTVProgram from 'components/Matchcenter/WidgetTVProgram';
import WidgetTeaser from 'components/Matchcenter/WidgetTeaser';
import SportsTabs from 'components/Matchcenter/SportsTabs';
// hooks
import useMatches from 'hooks/useMatches';
import useCompetitions from 'hooks/useCompetitions';
import usePolling from 'hooks/usePolling';
import useSearchTerm from 'hooks/useSearchTerm';
import useDateTabs from 'hooks/useDateTabs';
import useTeaserArticles from 'hooks/useTeaserArticles';
import useTVProgramEvents from 'hooks/useTVProgramEvents';
import useBreakpointMD from 'hooks/useBreakpointMD';
import useBreakpointXL from 'hooks/useBreakpointXL';
import useSportFilter from 'hooks/useSportFilter';
import useBreakpointLG from 'hooks/useBreakpointLG';
import {dateTabsTotal} from 'store/dateTabs/utils';
import WidgetBetPartners from 'components/Matchcenter/WidgetBetPartners';

import {sportNames} from 'utils/sportsUtils';
import getConfig from '../../getConfig';

import './styles.scss';

const {pollingRates} = getConfig();

const ppid = localStorage.getItem('__PPID') || null;

const getPageTitle = (searchTerm: SearchTerm, searchTermName?: string) =>
    searchTerm && searchTermName
        ? `${searchTermName} live στο Matchcenter | SPORT24`
        : 'Όλοι οι αγώνες live στο Matchcenter | SPORT24';

// TODO: fallback if URL invalid
// TODO: skeleton loader

/**
 * Displays the list of available matches grouping them by day or month.
 * Handles the business logic of fetching (and polling) the data when the 'searchTerm' changes,
 * it can be 'month' or 'days'
 *
 * Live matches have a different polling rate compared to matches in the past, to handle this the
 * business logic of fetching/polling the data has been split into smaller components via
 * the AsyncMatchListItem component, which is itself connected to the redux store via custom hooks.
 */
const MatchcenterPage: FC = () => {
    const location = useLocation();

    const isBreakpointMD = useBreakpointMD();
    const isBreakpointLG = useBreakpointLG();
    const isBreakpointXL = useBreakpointXL();

    const premiumAdRoot = useRef<HTMLElement | null>(null);

    const [matchesState, matchesActions] = useMatches();
    const [competitionsState, {getCompetitions}] = useCompetitions();
    const {data: competitions} = competitionsState;

    const [sportFilterStateValues, {setSportFilter}] = useSportFilter();
    const {filter} = sportFilterStateValues;

    const [searchTerm, searchTermName, searchTermSlug, setSearchTerm] = useSearchTerm(competitions);

    const [
        {tabsType: dateTabsType, tabs: dateTabs, selectedTabIndex: dateTabIndex},
        {setDateTabsType, setSelectedDateTab},
    ] = useDateTabs();
    const currentDate = useMemo(() => dateTabs[dateTabIndex].date, [dateTabs, dateTabIndex]);

    // BIQ: maybe move this into useDateTabs hook?
    /**
     * When user has searched, transform tabs
     */
    useEffect(() => {
        const type = searchTerm ? DateTabsType.MONTHS : DateTabsType.DAYS;

        setDateTabsType(type);
        if (dateTabs[dateTabIndex].date.length === 10 && type===DateTabsType.MONTHS) setSelectedDateTab();
        else if (dateTabs[dateTabIndex].date.length < 10 && type===DateTabsType.DAYS) setSelectedDateTab();
    }, [searchTerm, dateTabIndex, dateTabs, setDateTabsType, setSelectedDateTab]);

    const {withPolling, clearAllPollings} = usePolling();

    const matchesInDates = useMemo(
        () => selectMatches(searchTerm, filter, currentDate, matchesState?.data, competitions),
        [searchTerm, filter, currentDate, matchesState, competitions]
    );

    // polling versions of the matches-related actions
    const getMatchesByDate = withPolling(matchesActions.getMatchesByDate, pollingRates.sportEvents);

    const getMatchesBySportAndMonth = withPolling(
        matchesActions.getMatchesBySportAndMonth,
        pollingRates.sportEvents
    );

    const getMatchesByCompetitionIdAndMonth = withPolling(
        matchesActions.getMatchesByCompetitionIdAndMonth,
        pollingRates.sportEvents
    );

    const getMatchesByGroupId = withPolling(
        matchesActions.getMatchesByGroupId,
        pollingRates.sportEvents
    );

    const [teaserArticlesState, {getTeaserArticles}] = useTeaserArticles();
    const {loading: teaserArticleLoading, data: teaserArticles} = teaserArticlesState;

    const [tvProgramEventsState, {getTVProgramEvents}] = useTVProgramEvents();
    const {loading: tvProgramEventsLoading, data: tvProgramEvents} = tvProgramEventsState;

    const isApp = useMemo(() => {
        const query = new URLSearchParams(location.search);
        const appParam = query.get('sport24app');

        return appParam === 'true';
    }, [location]);

    /**
     * Article feeds should only be fetched once per component mount. No cleanup necessary.
     */
    useEffect(() => {
        const now = new Date();

        getTVProgramEvents(now);
        getTeaserArticles();
    }, []);

    /**
     * The following effect wraps up the business logic of choosing which polling function should
     * be performed, according to the user selection and the inputted search terms
     */
    useEffect(() => {
        if (!searchTerm) {
            if (filter === "top") {
                // getMatchesByGroupId("5ffc7cdc60a3477e43bb9843");
                getMatchesByDate(currentDate)
            }
            else getMatchesByDate(currentDate)
        } else {
            setSportFilter("top")
        } 
        

        if (searchTermIsSport(searchTerm)) {
            getMatchesBySportAndMonth(searchTerm as Sport, currentDate);
        }

        if (!searchTermIsSport(searchTerm) && competitions) {
            const competition = competitions.find(byTerm(searchTerm));
            if (competition) getMatchesByCompetitionIdAndMonth(competition.sportRadarId, currentDate);
        }

        return () => {
            getMatchesByDate.cancel();
            getMatchesBySportAndMonth.cancel();
            getMatchesByCompetitionIdAndMonth.cancel();
            getMatchesByGroupId.cancel()
        };
    }, [
        dateTabsType,
        filter,
        setSportFilter,
        getMatchesByGroupId,
        setSelectedDateTab,
        searchTerm,
        currentDate,
        competitions,
        getMatchesByDate,
        getMatchesBySportAndMonth,
        getMatchesByCompetitionIdAndMonth,
    ]);

    /**
     * When the component mounts fetch all the competitions data, if not preset yet
     */
    useEffect(() => {
        if (!competitionsState.data && !competitionsState.loading && !competitionsState.error) {
            getCompetitions();
        }
    }, [getCompetitions, competitionsState]);

    // get billboard ad root
    useEffect(() => {
        if (!premiumAdRoot.current){
            premiumAdRoot.current = document.getElementById('matchcenter-premium-root');
            premiumAdRoot.current?.classList.remove("adSlot-height--premium"); 
        }
            
    }, []);

    /**
     * Google Analytics
     */
    useEffect(() => {
        GA.initialize('UA-482127-1');
        GA.ga('send', 'pageview', {
            page: `${process.env.PUBLIC_URL}${location.pathname}${location.search}`,
            title: getPageTitle(searchTerm, searchTermName),
        });
    }, [location]); // don't include searchTerm & searchTermName as dependency to prevent duplicate pageviews when matchInfo is fetched asynchronously.


    /**
     * Google Analytics 4
     */
    useEffect(() => {
        GA4.initialize([
            {
                trackingId: 'G-CBFPZXDS6N',
            },

            {
                trackingId: 'G-NTXG7V0NM2',
            },
        ]);
        GA4.ga('send', 'pageview', {
            page: `${process.env.PUBLIC_URL}${location.pathname}${location.search}`,
            title: getPageTitle(searchTerm, searchTermName),
        });

    }, [location]); // don't include searchTerm & searchTermName as dependency to prevent duplicate pageviews when matchInfo is fetched asynchronously.

    /**
     * Google Ads Targeting and pushing PPID in every add request
     */
    useMemo(() => {
        const targetingKeywords = [searchTermSlug || 'matchcenter-home'] ;
        DFPManager.setTargetingArguments({keywords: targetingKeywords} );
        if (ppid) {
            DFPManager.getGoogletag().then((googletag) => {
                googletag.cmd.push(() => {
                  googletag.pubads().setPublisherProvidedId(ppid);
                });
            });
        }
        DFPManager.setCollapseEmptyDivs(false);

        /*  [TW-41160742] remove ads refresh

        DFPManager.refresh();
        
        */  

    }, [searchTermSlug]);

    /**
     * When component un-mounts clears all the generated polling functions
     */
    useEffect(() => clearAllPollings, [clearAllPollings]);



    return (
        <DFPSlotsProvider
            dfpNetworkId="4834629"
            targetingArguments={{
                path: window.location.href,
                isMobile: !isBreakpointMD,
                type: 'sec',
                // eslint-disable-next-line no-nested-ternary
                ...(searchTerm ?
                    sportNames.map(s => s.sport as string).includes(searchTerm)
                        ? {
                            category: [
                                ...(sportNames.find(s => s.sport === searchTerm)
                                    ? [sportNames.find(s => s.sport === searchTerm)?.eceSlug]
                                    : []),
                            ],
                        }
                        : {keywords: [searchTermSlug]} // NOTE: exported new searchTermSlug value from useSearchTerm.ts because searchTerm returns actual competition id => sr:competition:34480
                    : {}), // sports or competitions
            }}
            lazyLoad={{fetchMarginPercent: 500, renderMarginPercent: 10, mobileScaling: 2.0}}
            collapseEmptyDivs = {false}
        >
            <Ad
                root={premiumAdRoot.current}
                adUnit="sport24.gr/Matchcenter_hp_Billboard"
                scope="matchcenterpage"
                sizes={[
                    [970, 250],
                    [970, 90],
                    [728, 90],
                    [320, 100],
                    [320, 50],
                ]}
                sizeMapping={[
                    {
                        viewport: [breakpoints.xl, 0],
                        sizes: [
                            [970, 250],
                            [970, 90],
                        ],
                    },
                    {
                        viewport: [0, 0],
                        sizes: [],
                    }
                ]}
            />
            <div className="MatchcenterPage">
                <ReactTitle title={getPageTitle(searchTerm, searchTermName)}/>
                <header className="MatchcenterPage__header">
                    {!isApp && <h1 className="MatchcenterPage__title">MATCHCENTER</h1> }
                    <div className="MatchcenterPage__controlRow">
                        <SearchBar
                            term={searchTerm}
                            competitions={competitions}
                            onChange={value => {
                                setSearchTerm(value)
                                // setSelectedDateTab()
                            }}
                        />
                    </div>
                    {/* <a href='/olympic-games-2024-matchcenter/' target='_self'>
                        <button className="SportsTabs__tab olympics" type='button'>
                            <span className='SportsTabs__label'>ΟΛΥΜΠΙΑΚΟΙ ΑΓΩΝΕΣ</span>
                        </button>
                    </a> */}
                    {!searchTerm && (<div className="MatchcenterPage__controlRow">
                        <SportsTabs/>
                    </div>) }
                    <div className="MatchcenterPage__controlRow">
                        <DateTabs
                            searchTerm={searchTerm}
                            dateTabs={dateTabs}
                            tab={dateTabIndex}
                            setTab={setSelectedDateTab}
                        />
                    </div>
                </header>
                <main className="MatchcenterPage__main">
                    {isBreakpointXL && (
                        <section className="MatchcenterPage__left">
                            <Ad
                                adUnit="sport24.gr/Matchcenter_hp_300x250A"
                                scope="matchcenterpage"
                                sizes={[[300, 250]]}
                                sizeMapping={[
                                    {viewport: [breakpoints.lg, 0], sizes: [[300, 250]]},
                                    {viewport: [0, 0], sizes: []},
                                ]}
                            />
                            <WidgetTeaser loading={teaserArticleLoading} articles={teaserArticles}/>
                            <Ad
                                adUnit="sport24.gr/Matchcenter_hp_300x250C"
                                scope="matchcenterpage"
                                sizes={[
                                    [300, 600],
                                    [300, 250],
                                ]}
                                sizeMapping={[
                                    {
                                        viewport: [breakpoints.lg, 0],
                                        sizes: [
                                            [300, 600],
                                            [300, 250],
                                        ],
                                    },
                                    {viewport: [0, 0], sizes: []},
                                ]}
                                sticky
                                /* [TW-41160742] remove ads refresh
                                    autoRefresh
                                */
                            />
                        </section>
                    )}
                    <section className="MatchcenterPage__center">
                        <TabbedMatchLists
                            loading={matchesState.loading}
                            search={searchTerm}
                            matchesInDates={matchesInDates}
                            dateTab={dateTabIndex}
                            competitions={competitions}
                        />
                    </section>
                    {isBreakpointLG && (
                        /* FIXME: unmount this and destroy slot gracefully - maybe also remove sizemapping */
                        <section className="MatchcenterPage__right">
                            <WidgetTVProgram loading={tvProgramEventsLoading} tvData={tvProgramEvents}/>
                            <WidgetBetPartners/>
                            <Ad
                                adUnit="sport24.gr/Matchcenter_hp_300x250B"
                                scope="matchcenterpage"
                                sizes={[
                                    [300, 600],
                                    [300, 250],
                                ]}
                                sizeMapping={[
                                    {
                                        viewport: [breakpoints.lg, 0],
                                        sizes: [
                                            [300, 600],
                                            [300, 250],
                                        ],
                                    },
                                    {viewport: [0, 0], sizes: []},
                                ]}
                                sticky
                                /* [TW-41160742] remove ads refresh
                                    autoRefresh
                                */
                            />
                        </section>
                    )}
                </main>
                {!isBreakpointLG && (
                    <>
                        {/* FIXME: umnount his and destroy slot gracefully OR mount and setup */}
                        <Ad
                            adUnit="sport24.gr/Matchcenter_hp_320x50stuck"
                            scope="matchcenterpage"
                            sizes={[
                                [320, 50],
                                [320, 100],
                            ]}
                            stuck
                            /* [TW-41160742] remove ads refresh
                                autoRefresh
                            */
                        />
                    </>
                )}
            </div>
        </DFPSlotsProvider>
    );
};

export default React.memo(MatchcenterPage);
